// FLIP: when a layout change (the side panel opening or closing) moves the
// tiles of a grid, each tile is animated from where it was to where it now
// is, instead of the grid jumping between column counts. Positions are kept
// in page coordinates, so scrolling between two renders is not mistaken for
// a move. Tiles carry `data-flip="<stable id>"`.
import { useLayoutEffect, useRef, type RefObject } from "react";

const FLIP_MS = 240;

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

function measure(root: HTMLElement): Map<string, Box> {
  const out = new Map<string, Box>();
  root.querySelectorAll<HTMLElement>("[data-flip]").forEach((el) => {
    const r = el.getBoundingClientRect();
    out.set(el.dataset.flip as string, { x: r.left + window.scrollX, y: r.top + window.scrollY, w: r.width, h: r.height });
  });
  return out;
}

/** Animate the `[data-flip]` tiles under `root` whenever `layoutKey` changes. */
export function useFlip(root: RefObject<HTMLElement | null>, layoutKey: unknown): void {
  const last = useRef<Map<string, Box>>(new Map());
  const lastKey = useRef(layoutKey);

  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const now = measure(el);
    const changed = lastKey.current !== layoutKey;
    lastKey.current = layoutKey;
    const before = last.current;
    last.current = now;
    if (!changed || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    el.querySelectorAll<HTMLElement>("[data-flip]").forEach((tile) => {
      const id = tile.dataset.flip as string;
      const a = before.get(id);
      const b = now.get(id);
      if (!a || !b || b.w === 0 || b.h === 0) return;
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const sx = a.w / b.w;
      const sy = a.h / b.h;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(sx - 1) < 0.01 && Math.abs(sy - 1) < 0.01) return;
      tile.animate?.(
        [
          { transformOrigin: "top left", transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` },
          { transformOrigin: "top left", transform: "none" },
        ],
        { duration: FLIP_MS, easing: "cubic-bezier(0.2, 0.7, 0.2, 1)" },
      );
    });
  });
}
